import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { adminSupabase } from './adminSupabase'
import { effectiveRole } from './roles'

const AdminAuthContext = createContext(null)

// Dedicated admin-console session, independent of the end-user session in
// `./auth`. The admin console signs in through its own Supabase client scope
// (`opraiz-admin` storage key) so an admin can be logged in to the console
// while a separate end-user session (or none) exists in the same browser.
export function AdminAuthProvider({ children }) {
  const [adminUser, setAdminUser] = useState(null)
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    if (!adminSupabase) {
      setInitializing(false)
      return
    }
    let active = true
    adminSupabase.auth.getUser().then(({ data: { user } }) => {
      if (!active) return
      setAdminUser(user)
      setInitializing(false)
    })

    const { data: { subscription } } = adminSupabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return
      setAdminUser(session?.user ?? null)
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [])

  const refresh = useCallback(async () => {
    if (!adminSupabase) return
    const { data } = await adminSupabase.auth.getUser()
    if (data?.user) setAdminUser(data.user)
  }, [])

  const signOut = useCallback(async () => {
    await adminSupabase?.auth.signOut()
    setAdminUser(null)
  }, [])

  const value = {
    adminUser,
    adminRole: adminUser ? effectiveRole(adminUser) : 'member',
    initializing,
    refresh,
    signOut,
  }

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  return ctx
}